import { ReactNode } from 'react';
import { ListButton, ReferenceField, Show, TextField, TopToolbar, useRecordContext } from 'react-admin';
import { Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { __t } from '../../i18n/t';
import { InternalOffer, InternalSuggestionStation } from '../../models/attendantGoalOffer';
import { AttendantGoalType } from '../../models/attendantGoalType';
import { goalLevelLabel } from '../../components/AttendantGoalOfferCard/offerCopy';
import InternalGate from './InternalGate';
import {
  endedReasonLabel, fmtDateTime, fmtDay, insightKindLabel, insightStatusLabel, optionLine, rolloutColor, rolloutLabel,
  shortId, statusColor, statusLabel, triggerLabel,
} from './labels';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
    {children}
  </Paper>
);

// RaLayout-root é fit-content: tabela larga só rola aqui dentro se o contêiner não entrar na conta da largura.
const Scroll = ({ children }: { children: ReactNode }) => (
  <Box sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>{children}</Box>
);

const Empty = ({ text }: { text: string }) => <Typography variant="body2" color="text.secondary">{text}</Typography>;

/**
 * Entidade com tela Show vira link. Quando o getMany não resolve (o funcionário é de uma rede diferente
 * da selecionada no topo, por exemplo) sobra o início do id.
 */
const Ref = ({ id, reference }: { id?: string | null; reference: string }) => (
  id ? (
    <ReferenceField record={{ id, ref: id }} source="ref" reference={reference} link="show" emptyText={shortId(id)}>
      <TextField source="name" />
    </ReferenceField>
  ) : null
);

const TimelineRow = ({ label, value, who, extra }: { label: string; value: ReactNode; who?: string | null; extra?: ReactNode }) => (
  <TableRow>
    <TableCell sx={{ width: 220, fontWeight: 500 }}>{label}</TableCell>
    <TableCell sx={{ width: 170, whiteSpace: 'nowrap' }}>{value}</TableCell>
    <TableCell>
      {who && (
        <Box component="span" sx={{ display: 'inline-flex', gap: 0.5 }}>
          <span>{__t('por')}</span>
          <Ref id={who} reference="employees" />
        </Box>
      )}
      {extra}
    </TableCell>
  </TableRow>
);

const Header = ({ r }: { r: InternalOffer }) => (
  <Section title={__t('Convite')}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
      <Typography component="span" variant="h6"><Ref id={r.chainId} reference="chains" /></Typography>
      <Chip size="small" color={statusColor(r.status)} label={statusLabel(r.status, r.endedReason)} />
      {r.rollout && <Chip size="small" variant="outlined" color={rolloutColor(r.rollout)} label={rolloutLabel(r.rollout)} />}
      <Typography variant="body2" color="text.secondary">
        {__t('Período-base: %{ini} a %{fim}', { ini: fmtDay(r.baselineStart), fim: fmtDay(r.baselineEnd) })}
        {' · '}{__t('sugestão v%{v}', { v: r.suggestionVersion })}
      </Typography>
    </Box>
    <Table size="small" sx={{ maxWidth: 800 }}>
      <TableBody>
        <TimelineRow label={__t('Criado')} value={fmtDateTime(r.createdAt)} />
        {r.sentByEmployeeId && (
          <TimelineRow label={__t('Enviado por')} value={<Ref id={r.sentByEmployeeId} reference="employees" />} />
        )}
        <TimelineRow label={__t('Visto pela primeira vez')} value={fmtDateTime(r.firstSeenAt)} />
        <TimelineRow label={__t('Agora não')} value={fmtDateTime(r.dismissedAt)} who={r.dismissedByEmployeeId} />
        <TimelineRow label={__t('Ativado')} value={fmtDateTime(r.activatedAt)} who={r.activatedByEmployeeId} />
        <TimelineRow label={__t('Fim da degustação')} value={fmtDateTime(r.trialEndsAt)} />
        <TimelineRow label={__t('Aviso do dia 51')} value={fmtDateTime(r.endingNotifiedAt)} />
        <TimelineRow
          label={__t('Encerrado')}
          value={fmtDateTime(r.endedAt)}
          extra={r.endedReason ? endedReasonLabel(r.endedReason) : null}
        />
        <TimelineRow label={__t('Pediu para continuar')} value={fmtDateTime(r.continueRequestedAt)} who={r.continueRequestedByEmployeeId} />
        <TimelineRow label={__t('Última semana avisada')} value={fmtDay(r.lastWeeklyNotifiedWeek)} />
      </TableBody>
    </Table>
  </Section>
);

const dash = (v: ReactNode | null | undefined) => (v === null || v === undefined ? '—' : v);

const StationsTable = ({ r }: { r: InternalOffer }) => {
  const stations = r.triggers?.stations ?? [];
  const suggestions = new Map((r.suggestion?.stations ?? []).map((s): [string, InternalSuggestionStation] => [s.placeId, s]));
  if (!stations.length) { return <Section title={__t('Postos analisados')}><Empty text={__t('Nenhum posto analisado.')} /></Section>; }

  return (
    <Section title={__t('Postos analisados')}>
      <Scroll>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{__t('Posto')}</TableCell>
              <TableCell>{__t('Gatilhos')}</TableCell>
              <TableCell align="right">{__t('Frentistas elegíveis')}</TableCell>
              <TableCell align="right">{__t('Na cauda')}</TableCell>
              <TableCell align="right">{__t('Rendimento da cauda')}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{__t('% app do posto')}</TableCell>
              <TableCell align="right">{__t('Abaixo na fidelização')}</TableCell>
              <TableCell>{__t('Tipo de meta')}</TableCell>
              <TableCell>{__t('Nível recomendado')}</TableCell>
              <TableCell>{__t('Opções')}</TableCell>
              <TableCell>{__t('Nível escolhido')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stations.map(s => {
              const sg = suggestions.get(s.placeId);
              const chosen = r.levelsChosen?.[s.placeId];
              return (
                <TableRow key={s.placeId}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><Ref id={s.placeId} reference="stations" /></TableCell>
                  <TableCell>
                    {s.triggers?.length ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        {s.triggers.map(t => <Chip key={t} size="small" label={triggerLabel(t)} />)}
                      </Box>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">{dash(s.eligibleAttendants)}</TableCell>
                  <TableCell align="right">{dash(s.tailSize)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {s.tailIndexPct === null || s.tailIndexPct === undefined ? '—' : __t('%{p}% do que a equipe rende', { p: s.tailIndexPct })}
                  </TableCell>
                  <TableCell align="right">{s.stationAppPct === null || s.stationAppPct === undefined ? '—' : `${s.stationAppPct}%`}</TableCell>
                  <TableCell align="right">{dash(s.loyaltyLowCount)}</TableCell>
                  <TableCell>{sg ? AttendantGoalType.translateType(sg.targetType) : '—'}</TableCell>
                  <TableCell>{sg?.recommended ? goalLevelLabel(sg.recommended) : '—'}</TableCell>
                  <TableCell>
                    {sg?.options?.length
                      ? sg.options.map(o => <Typography key={o.level} variant="body2" sx={{ whiteSpace: 'nowrap' }}>{optionLine(sg.targetType, o)}</Typography>)
                      : '—'}
                  </TableCell>
                  <TableCell>{chosen ? goalLevelLabel(chosen) : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Scroll>
    </Section>
  );
};

const Boards = ({ r }: { r: InternalOffer }) => (
  <Section title={__t('Quadros criados')}>
    {r.boardIds?.length
      ? r.boardIds.map(id => <Typography key={id} variant="body2"><Ref id={id} reference="attendant-goal-boards" /></Typography>)
      : <Empty text={__t('Nenhum quadro criado.')} />}
  </Section>
);

const Insights = ({ r }: { r: InternalOffer }) => (
  <Section title={__t('Leituras da IA')}>
    {r.insights?.length ? (
      <Scroll>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{__t('Tipo')}</TableCell>
              <TableCell>{__t('Idioma')}</TableCell>
              <TableCell>{__t('Semana')}</TableCell>
              <TableCell>{__t('Situação')}</TableCell>
              <TableCell align="right">{__t('Tentativas')}</TableCell>
              <TableCell align="right">{__t('Custo (US$)')}</TableCell>
              <TableCell>{__t('Gerada em')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {r.insights.map(i => (
              <TableRow key={i.id}>
                <TableCell>{insightKindLabel(i.kind)}</TableCell>
                <TableCell>{i.locale}</TableCell>
                <TableCell>{fmtDay(i.weekStart)}</TableCell>
                <TableCell>{insightStatusLabel(i.status)}</TableCell>
                <TableCell align="right">{i.attempts ?? 0}</TableCell>
                <TableCell align="right">{Number(i.costUsd ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</TableCell>
                <TableCell>{fmtDateTime(i.generatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scroll>
    ) : <Empty text={__t('Nenhuma leitura da IA para este convite.')} />}
  </Section>
);

const Content = () => {
  const r = useRecordContext<InternalOffer>();
  if (!r) { return null; }
  return (
    <Box sx={{ mt: 2 }}>
      <Header r={r} />
      <StationsTable r={r} />
      <Boards r={r} />
      <Insights r={r} />
    </Box>
  );
};

const AttendantGoalOfferShow = () => (
  <InternalGate>
    <Show title="Convite de Meta de Frentista" actions={<TopToolbar><ListButton /></TopToolbar>}>
      <Content />
    </Show>
  </InternalGate>
);

export default AttendantGoalOfferShow;
